import { useCallback, useEffect, useRef, useState } from 'react';
import { Coffee, X, Check, Loader2, Timer, ChevronDown } from 'lucide-react';
import { io } from 'socket.io-client';
import { API_BASE_URL, SOCKET_URL, cn } from '../../lib/utils';
import { useAppSelector } from '../../store';
import { canAccessCommand } from '../../lib/permissions';
import { useActiveEvent } from '../../contexts/EventContext';
import type { BreakRequest, BreakTimerMode } from '../../types';

export default function BreakRequestAlert() {
  const { token, user } = useAppSelector((state) => state.auth);
  const { activeEventId } = useActiveEvent();
  const [queue, setQueue] = useState<BreakRequest[]>([]);
  const [current, setCurrent] = useState<BreakRequest | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [commandNotes, setCommandNotes] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [timerMode, setTimerMode] = useState<BreakTimerMode>('countup');
  const [countdownMinutes, setCountdownMinutes] = useState('15');
  const [responding, setResponding] = useState(false);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const isCommandUser = canAccessCommand(user?.role);

  const loadPending = useCallback(async () => {
    if (!token || !isCommandUser) return;
    try {
      const params = new URLSearchParams({ status: 'pending' });
      if (activeEventId) params.set('eventId', activeEventId);
      const res = await fetch(`${API_BASE_URL}/break-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const pending: BreakRequest[] = data.breakRequests || [];
      if (!initializedRef.current) {
        pending.forEach((r) => seenIdsRef.current.add(r.id));
        initializedRef.current = true;
        if (pending.length > 0) {
          setQueue(pending);
          setCurrent(pending[0]);
        }
        return;
      }
      const fresh = pending.filter((r) => !seenIdsRef.current.has(r.id));
      if (fresh.length === 0) return;
      fresh.forEach((r) => seenIdsRef.current.add(r.id));
      setQueue((prev) => {
        const merged = [...prev];
        fresh.forEach((r) => {
          if (!merged.some((m) => m.id === r.id)) merged.push(r);
        });
        return merged;
      });
      setCurrent((prev) => prev || fresh[0]);
      setCollapsed(false);
    } catch { /* ignore */ }
  }, [token, isCommandUser, activeEventId]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    if (!token || !isCommandUser) return;
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token },
    });

    socket.on('break:requested', (breakRequest: BreakRequest) => {
      if (activeEventId && breakRequest.eventId !== activeEventId) return;
      if (seenIdsRef.current.has(breakRequest.id)) return;
      seenIdsRef.current.add(breakRequest.id);
      setQueue((prev) => (prev.some((r) => r.id === breakRequest.id) ? prev : [...prev, breakRequest]));
      setCurrent((prev) => prev || breakRequest);
      setCollapsed(false);
    });

    return () => { socket.disconnect(); };
  }, [token, isCommandUser, activeEventId]);

  const resetForm = () => {
    setCommandNotes('');
    setDeclineReason('');
    setTimerMode('countup');
    setCountdownMinutes('15');
  };

  const advanceQueue = (handledId: string) => {
    setQueue((prev) => {
      const next = prev.filter((r) => r.id !== handledId);
      setCurrent(next[0] || null);
      return next;
    });
    resetForm();
  };

  const respond = async (action: 'approve' | 'decline') => {
    if (!currentItem || !token) return;
    setResponding(true);
    try {
      const body: Record<string, unknown> = {
        action,
        commandNotes: commandNotes.trim() || undefined,
      };
      if (action === 'decline') {
        body.declineReason = declineReason.trim() || undefined;
      } else {
        body.timerMode = timerMode;
        if (timerMode === 'countdown') {
          body.countdownMinutes = Number(countdownMinutes);
        }
      }

      const res = await fetch(`${API_BASE_URL}/break-requests/${currentItem.id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to respond');
      }
      advanceQueue(currentItem.id);
    } catch { /* ignore */ } finally {
      setResponding(false);
    }
  };

  const dismiss = () => {
    if (!current) return;
    setDismissedIds((prev) => new Set(prev).add(current.id));
    setCollapsed(true);
  };

  const visible = current && !dismissedIds.has(current.id)
    ? current
    : queue.find((r) => !dismissedIds.has(r.id)) || null;

  if (!isCommandUser || !visible) {
    if (isCommandUser && queue.some((r) => !dismissedIds.has(r.id)) && collapsed) {
      const peek = queue.find((r) => !dismissedIds.has(r.id));
      if (peek) {
        return (
          <button
            onClick={() => { setCurrent(peek); setCollapsed(false); }}
            className="fixed top-20 right-4 z-[99990] flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-500/30 bg-card/95 shadow-lg text-xs font-semibold text-purple-300 hover:border-purple-400/50 transition-colors"
          >
            <Coffee className="w-4 h-4" />
            Break request · {peek.resourceName}
            {queue.filter((r) => !dismissedIds.has(r.id)).length > 1 && (
              <span className="text-muted-foreground">
                (+{queue.filter((r) => !dismissedIds.has(r.id)).length - 1})
              </span>
            )}
          </button>
        );
      }
    }
    return null;
  }

  const currentItem = visible;

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed top-20 right-4 z-[99990] flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-500/30 bg-card/95 shadow-lg text-xs font-semibold text-purple-300 hover:border-purple-400/50 transition-colors"
      >
        <Coffee className="w-4 h-4" />
        Break request · {currentItem.resourceName}
        {queue.filter((r) => !dismissedIds.has(r.id)).length > 1 && (
          <span className="text-muted-foreground">
            (+{queue.filter((r) => !dismissedIds.has(r.id)).length - 1})
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed top-20 right-4 z-[99990] w-[min(100vw-2rem,380px)] animate-in slide-in-from-right-4 duration-200">
      <div className="bg-card/95 backdrop-blur-md border border-purple-500/35 rounded-xl overflow-hidden shadow-xl">
        <div className="px-4 py-3 flex items-center justify-between border-b border-purple-500/15 bg-purple-500/10">
          <div className="flex items-center gap-2 min-w-0">
            <Coffee className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground truncate">Break request</p>
              <p className="text-[10px] text-muted-foreground">
                {queue.filter((r) => !dismissedIds.has(r.id)).length > 1
                  ? `${queue.filter((r) => !dismissedIds.has(r.id)).length} pending`
                  : currentItem.resourceName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={dismiss}
              className="w-7 h-7 rounded-md border border-border hover:bg-accent flex items-center justify-center"
              title="Minimise"
            >
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
            <button
              onClick={dismiss}
              className="w-7 h-7 rounded-md border border-border hover:bg-accent flex items-center justify-center"
              title="Dismiss notification"
            >
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="rounded-lg border border-border bg-accent/30 p-3 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm font-bold text-foreground">{currentItem.resourceName}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-bold uppercase shrink-0">
                {currentItem.typeLabel}
              </span>
            </div>
            <p className="text-xs text-foreground">
              <span className="text-muted-foreground">Officer:</span> {currentItem.userName}
            </p>
            <p className="text-[10px] text-muted-foreground">
              Requested {new Date(currentItem.requestedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Officer notes</label>
            <div className="mt-1 px-3 py-2 rounded-lg bg-background/60 border border-border text-xs text-foreground min-h-[44px]">
              {currentItem.notes?.trim() ? currentItem.notes : <span className="text-muted-foreground italic">No notes provided</span>}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Control notes</label>
            <textarea
              rows={2}
              placeholder="Notes for the officer or log..."
              value={commandNotes}
              onChange={(e) => setCommandNotes(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-background border border-border text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Timer className="w-3 h-3" /> Break timer (on approve)
            </label>
            <div className="mt-1.5 flex gap-2">
              <button
                type="button"
                onClick={() => setTimerMode('countdown')}
                className={cn(
                  'flex-1 py-2 rounded-lg text-[11px] font-semibold border transition-colors',
                  timerMode === 'countdown'
                    ? 'border-purple-400/40 bg-purple-500/15 text-purple-300'
                    : 'border-border text-muted-foreground hover:border-purple-500/20'
                )}
              >
                Count down
              </button>
              <button
                type="button"
                onClick={() => setTimerMode('countup')}
                className={cn(
                  'flex-1 py-2 rounded-lg text-[11px] font-semibold border transition-colors',
                  timerMode === 'countup'
                    ? 'border-purple-400/40 bg-purple-500/15 text-purple-300'
                    : 'border-border text-muted-foreground hover:border-purple-500/20'
                )}
              >
                Count up
              </button>
            </div>
            {timerMode === 'countdown' && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={480}
                  value={countdownMinutes}
                  onChange={(e) => setCountdownMinutes(e.target.value)}
                  className="w-20 px-2 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                />
                <span className="text-[11px] text-muted-foreground">minutes</span>
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Decline reason (optional)</label>
            <input
              type="text"
              placeholder="If declining..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg bg-background border border-border text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-500/30"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => respond('decline')}
              disabled={responding}
              className={cn(
                'flex-1 py-2.5 rounded-lg border border-red-500/30 text-red-400 font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-red-500/10',
                responding && 'opacity-50'
              )}
            >
              {responding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              Decline
            </button>
            <button
              onClick={() => respond('approve')}
              disabled={responding}
              className={cn(
                'flex-1 py-2.5 rounded-lg bg-purple-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-purple-400',
                responding && 'opacity-50'
              )}
            >
              {responding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
